/**
 * Mock Classroom Data
 *
 * Realistic demo dataset for showcasing the full Classroom Mode.
 * Simulates a Swedish school scenario with multiple classrooms,
 * subjects (sections), pages per subject, and student/teacher members.
 */

// ─── Members ────────────────────────────────────────────────────────────────

const MOCK_TEACHERS = [
  { id: 'demo-teacher-1', name: 'Anna Lindberg', firstName: 'Anna', lastName: 'Lindberg', role: 'teacher' },
  { id: 'demo-teacher-2', name: 'Erik Johansson', firstName: 'Erik', lastName: 'Johansson', role: 'teacher' },
  { id: 'demo-teacher-3', name: 'Maria Svensson', firstName: 'Maria', lastName: 'Svensson', role: 'teacher' },
  { id: 'demo-teacher-4', name: 'Lars Nilsson', firstName: 'Lars', lastName: 'Nilsson', role: 'teacher' },
];

const MOCK_STUDENTS_9A = [
  { id: 'demo-student-1', name: 'Saga Eriksson', firstName: 'Saga', lastName: 'Eriksson', role: 'reader' },
  { id: 'demo-student-2', name: 'Noah Andersson', firstName: 'Noah', lastName: 'Andersson', role: 'reader' },
  { id: 'demo-student-3', name: 'Wilma Larsson', firstName: 'Wilma', lastName: 'Larsson', role: 'reader' },
  { id: 'demo-student-4', name: 'Liam Pettersson', firstName: 'Liam', lastName: 'Pettersson', role: 'reader' },
  { id: 'demo-student-5', name: 'Ella Olsson', firstName: 'Ella', lastName: 'Olsson', role: 'reader' },
  { id: 'demo-student-6', name: 'Oliver Gustafsson', firstName: 'Oliver', lastName: 'Gustafsson', role: 'reader' },
  { id: 'demo-student-7', name: 'Astrid Nilsson', firstName: 'Astrid', lastName: 'Nilsson', role: 'reader' },
  { id: 'demo-student-8', name: 'Hugo Persson', firstName: 'Hugo', lastName: 'Persson', role: 'reader' },
  { id: 'demo-student-9', name: 'Freja Karlsson', firstName: 'Freja', lastName: 'Karlsson', role: 'reader' },
  { id: 'demo-student-10', name: 'Lucas Svensson', firstName: 'Lucas', lastName: 'Svensson', role: 'reader' },
  { id: 'demo-student-11', name: 'Maja Jonsson', firstName: 'Maja', lastName: 'Jonsson', role: 'reader' },
  { id: 'demo-student-12', name: 'Elias Berg', firstName: 'Elias', lastName: 'Berg', role: 'reader' },
  { id: 'demo-student-13', name: 'Alma Lindqvist', firstName: 'Alma', lastName: 'Lindqvist', role: 'reader' },
  { id: 'demo-student-14', name: 'William Holm', firstName: 'William', lastName: 'Holm', role: 'reader' },
  { id: 'demo-student-15', name: 'Ebba Nyström', firstName: 'Ebba', lastName: 'Nyström', role: 'reader' },
];

const MOCK_STUDENTS_9B = [
  { id: 'demo-student-20', name: 'Vera Lindgren', firstName: 'Vera', lastName: 'Lindgren', role: 'reader' },
  { id: 'demo-student-21', name: 'Axel Engström', firstName: 'Axel', lastName: 'Engström', role: 'reader' },
  { id: 'demo-student-22', name: 'Signe Fransson', firstName: 'Signe', lastName: 'Fransson', role: 'reader' },
  { id: 'demo-student-23', name: 'Leo Ek', firstName: 'Leo', lastName: 'Ek', role: 'reader' },
  { id: 'demo-student-24', name: 'Ines Åberg', firstName: 'Ines', lastName: 'Åberg', role: 'reader' },
  { id: 'demo-student-25', name: 'Filip Hedlund', firstName: 'Filip', lastName: 'Hedlund', role: 'reader' },
  { id: 'demo-student-26', name: 'Tuva Magnusson', firstName: 'Tuva', lastName: 'Magnusson', role: 'reader' },
  { id: 'demo-student-27', name: 'Theo Wallin', firstName: 'Theo', lastName: 'Wallin', role: 'reader' },
  { id: 'demo-student-28', name: 'Sigrid Blom', firstName: 'Sigrid', lastName: 'Blom', role: 'reader' },
  { id: 'demo-student-29', name: 'Edvin Sjöberg', firstName: 'Edvin', lastName: 'Sjöberg', role: 'reader' },
  { id: 'demo-student-30', name: 'Liv Sandberg', firstName: 'Liv', lastName: 'Sandberg', role: 'reader' },
  { id: 'demo-student-31', name: 'Noel Dahlgren', firstName: 'Noel', lastName: 'Dahlgren', role: 'reader' },
];

const MOCK_STUDENTS_TUTORING = [
  { id: 'demo-student-40', name: 'Alva Sundström', firstName: 'Alva', lastName: 'Sundström', role: 'reader' },
  { id: 'demo-student-41', name: 'Isak Forsberg', firstName: 'Isak', lastName: 'Forsberg', role: 'reader' },
  { id: 'demo-student-42', name: 'Klara Björk', firstName: 'Klara', lastName: 'Björk', role: 'reader' },
  { id: 'demo-student-43', name: 'Arvid Ström', firstName: 'Arvid', lastName: 'Ström', role: 'reader' },
];

// ─── Pages per subject ──────────────────────────────────────────────────────

const createSubjectPages = (subjectName, subjectId, options = {}) => {
  const {
    assignmentProgress = null,
    quizProgress = null,
    isLocked = false,
    weekNumber = 6,
    discussionMessages = 0,
    fileCount = 0,
    pastSessionCount = 3,
  } = options;

  const teacherName = getTeacherForSubject(subjectId);

  // Generate past recorded sessions (weeks ago)
  const pastSessions = Array.from({ length: pastSessionCount }, (_, i) => {
    const weeksAgo = pastSessionCount - i; // 3 weeks ago, 2 weeks ago, 1 week ago
    const sessionDate = getPastSessionDate(subjectId, weeksAgo);
    return {
      id: `${subjectId}-live-w${weekNumber - weeksAgo}`,
      name: `${subjectName} - Lesson: Week ${weekNumber - weeksAgo}`,
      type: 'webinar',
      status: 'recorded',
      hasRecording: true,
      recordingDuration: `${55 + Math.floor(Math.random() * 30)}min`,
      scheduledDate: sessionDate,
      teacherName,
      topic: getLessonTopic(subjectName, weekNumber - weeksAgo),
      viewCount: Math.floor(Math.random() * 12) + 3,
      progress: i === pastSessionCount - 1 ? 0.6 + Math.random() * 0.4 : 1, // most recent maybe not fully watched
    };
  });

  // The next upcoming session
  const upcomingSession = {
    id: `${subjectId}-live-next`,
    name: `${subjectName} - Lesson: Week ${weekNumber}`,
    type: 'webinar',
    status: 'upcoming',
    hasRecording: false,
    scheduledDate: getNextSessionDate(subjectId),
    teacherName,
    topic: getLessonTopic(subjectName, weekNumber),
    progress: null,
  };

  return {
    id: subjectId,
    name: subjectName,
    pages: [
      upcomingSession,
      ...pastSessions,
      {
        id: `${subjectId}-discussion`,
        name: `${subjectName} - Discussion & Files`,
        type: 'discussion',
        discussionMessages,
        fileCount,
        lastActivity: getLastActivity(subjectId),
      },
      {
        id: `${subjectId}-assignment-1`,
        name: `${subjectName} - Assignment: Week ${weekNumber}`,
        type: 'task',
        progress: assignmentProgress,
        isLocked,
        dueDate: getDueDate(weekNumber),
        description: getAssignmentDescription(subjectName, weekNumber),
      },
      {
        id: `${subjectId}-quiz-1`,
        name: `${subjectName} - Quiz: Chapter ${weekNumber - 3}`,
        type: 'quiz',
        progress: quizProgress,
        isLocked,
        questionCount: Math.floor(Math.random() * 10) + 5,
        timeLimit: '30 min',
      },
    ],
  };
};

// ─── Helper functions ───────────────────────────────────────────────────────

const getNextSessionDate = (subjectId) => {
  const dayMap = {
    'demo-9a-math': 1,
    'demo-9a-swedish': 2,
    'demo-9a-english': 3,
    'demo-9a-history': 4,
    'demo-9a-science': 5,
    'demo-9b-math': 1,
    'demo-9b-swedish': 3,
    'demo-9b-science': 4,
  };
  const hourMap = {
    'demo-9a-math': 9,
    'demo-9a-swedish': 10,
    'demo-9a-english': 13,
    'demo-9a-history': 9,
    'demo-9a-science': 11,
    'demo-9b-math': 11,
    'demo-9b-swedish': 14,
    'demo-9b-science': 9,
  };
  const now = new Date();
  const day = dayMap[subjectId] ?? 1;
  const hour = hourMap[subjectId] ?? 9;
  const result = new Date(now);
  const currentDay = now.getDay();
  const daysUntil = (day - currentDay + 7) % 7 || 7;
  result.setDate(now.getDate() + daysUntil);
  result.setHours(hour, 0, 0, 0);
  return result.toISOString();
};

const getPastSessionDate = (subjectId, weeksAgo) => {
  // Same day/time as the next session, but weeksAgo weeks in the past
  const nextDate = new Date(getNextSessionDate(subjectId));
  nextDate.setDate(nextDate.getDate() - weeksAgo * 7);
  return nextDate.toISOString();
};

const getLessonTopic = (subjectName, weekNumber) => {
  const topics = {
    Mathematics: [
      'Introduction to Algebra',
      'Linear Equations',
      'Inequalities & Absolute Values',
      'Systems of Equations',
      'Quadratic Expressions',
      'Factoring Polynomials',
      'Quadratic Formula & Applications',
    ],
    Swedish: ['Textanalys — Introduktion', 'Argumenterande text', 'Referatteknik', 'Källkritik', 'Muntlig presentation', 'Debattartikel', 'Novellanalys'],
    English: [
      'Reading Comprehension Strategies',
      'Vocabulary Building — Context Clues',
      'Narrative Writing',
      'Formal vs Informal Register',
      'Persuasive Essays',
      'Idioms & Expressions',
      'Book Discussion — Chapter Review',
    ],
    History: [
      'The Age of Exploration',
      'The Reformation',
      'The Thirty Years War — Causes',
      'The Thirty Years War — Key Battles',
      'The Peace of Westphalia',
      'The Swedish Empire',
      'Industrialization Begins',
    ],
    Science: [
      'Atoms & Molecules',
      'Chemical Reactions',
      'The Periodic Table',
      'Acids & Bases',
      'Lab Techniques & Safety',
      'Energy Transfer',
      'Electricity & Circuits',
    ],
    'Reading Comprehension': [
      'Active Reading Strategies',
      'Finding the Main Idea',
      'Making Inferences',
      'Context Clues & Vocabulary',
      'Summarizing & Paraphrasing',
      'Comparing Texts',
      'Critical Analysis',
    ],
  };
  // Match partial names like "Mathematics — Extra Support" → "Mathematics"
  const match = Object.keys(topics).find((key) => subjectName.startsWith(key));
  const subjectTopics = topics[match] ?? topics['Mathematics'];
  return subjectTopics[(weekNumber - 1) % subjectTopics.length];
};

const getTeacherForSubject = (subjectId) => {
  const map = {
    'demo-9a-math': 'Anna Lindberg',
    'demo-9a-swedish': 'Erik Johansson',
    'demo-9a-english': 'Anna Lindberg',
    'demo-9a-history': 'Maria Svensson',
    'demo-9a-science': 'Lars Nilsson',
    'demo-9b-math': 'Anna Lindberg',
    'demo-9b-swedish': 'Erik Johansson',
    'demo-9b-science': 'Lars Nilsson',
    'demo-tutoring-math': 'Anna Lindberg',
    'demo-tutoring-reading': 'Erik Johansson',
  };
  return map[subjectId] ?? 'Anna Lindberg';
};

const getLastActivity = (subjectId) => {
  const hoursAgo = { 'demo-9a-math': 2, 'demo-9a-swedish': 5, 'demo-9a-english': 12 };
  const h = hoursAgo[subjectId] ?? Math.floor(Math.random() * 24) + 1;
  return new Date(Date.now() - h * 3600000).toISOString();
};

const getDueDate = (weekNumber) => {
  const now = new Date();
  const daysUntilFriday = (5 - now.getDay() + 7) % 7 || 7;
  const friday = new Date(now);
  friday.setDate(now.getDate() + daysUntilFriday);
  friday.setHours(23, 59, 0, 0);
  return friday.toISOString();
};

const getAssignmentDescription = (subject, week) => {
  const descriptions = {
    Mathematics: `Complete exercises 4.1–4.8 from Chapter ${week - 3}. Show all working. Submit as PDF or photo.`,
    Swedish: `Write a 400-word argumentative essay on the topic assigned in class. Use formal language and cite at least two sources.`,
    English: `Read Chapter ${week - 2} of the novel and answer the comprehension questions on page ${week * 12}. Prepare discussion points for next class.`,
    History: `Research and write a 300-word summary of the historical event discussed in the lecture. Include a timeline of key dates.`,
    Science: `Complete the lab report for experiment ${week - 4}. Include hypothesis, method, results, and conclusion sections.`,
  };
  return descriptions[subject] ?? `Complete the assigned work for week ${week}.`;
};

// ─── Discussion mock messages ───────────────────────────────────────────────

export const MOCK_DISCUSSION_MESSAGES = {
  'demo-9a-math-discussion': [
    { id: 'msg-1', author: 'Saga Eriksson', text: 'Can someone explain exercise 4.3? I keep getting a different answer.', time: '2h ago', avatar: 'S' },
    {
      id: 'msg-2',
      author: 'Anna Lindberg',
      text: 'Great question Saga! Remember to factor out the common denominator first. I recorded a quick video explanation — check Files.',
      time: '1h ago',
      avatar: 'A',
      isTeacher: true,
    },
    {
      id: 'msg-3',
      author: 'Noah Andersson',
      text: 'That helped, tack Anna! I also had trouble with 4.5, is the answer supposed to be negative?',
      time: '45min ago',
      avatar: 'N',
    },
    {
      id: 'msg-4',
      author: 'Wilma Larsson',
      text: 'Yes Noah, the answer is -3.5. You need to flip the inequality when dividing by negative numbers.',
      time: '30min ago',
      avatar: 'W',
    },
    {
      id: 'msg-5',
      author: 'Anna Lindberg',
      text: 'Exactly right Wilma! Everyone, remember this rule — it will be on the quiz on Friday.',
      time: '15min ago',
      avatar: 'A',
      isTeacher: true,
    },
  ],
  'demo-9a-swedish-discussion': [
    {
      id: 'msg-10',
      author: 'Erik Johansson',
      text: "Today's essay topic is posted. Remember: thesis statement in the first paragraph. Deadline is Friday.",
      time: '4h ago',
      avatar: 'E',
      isTeacher: true,
    },
    {
      id: 'msg-11',
      author: 'Ella Olsson',
      text: 'Can we write about any topic within the theme or does it have to be one of the three you listed?',
      time: '3h ago',
      avatar: 'E',
    },
    {
      id: 'msg-12',
      author: 'Erik Johansson',
      text: 'Stick to the three listed topics please. This makes peer review more manageable.',
      time: '2h ago',
      avatar: 'E',
      isTeacher: true,
    },
    {
      id: 'msg-13',
      author: 'Freja Karlsson',
      text: 'I uploaded my draft to the Files section. Would appreciate any feedback before Friday!',
      time: '1h ago',
      avatar: 'F',
    },
  ],
  'demo-9a-english-discussion': [
    {
      id: 'msg-20',
      author: 'Anna Lindberg',
      text: 'Class, the vocabulary test has been moved to next Tuesday. Use the extra time to review Chapters 5-6.',
      time: '6h ago',
      avatar: 'A',
      isTeacher: true,
    },
    { id: 'msg-21', author: 'Liam Pettersson', text: 'Will the test cover idioms too or just the word list?', time: '5h ago', avatar: 'L' },
    { id: 'msg-22', author: 'Anna Lindberg', text: 'Both! Check the study guide I uploaded last week.', time: '4h ago', avatar: 'A', isTeacher: true },
  ],
  'demo-9a-history-discussion': [
    {
      id: 'msg-30',
      author: 'Maria Svensson',
      text: "For those who missed today's session — the recording is now available in Live Lessons. Watch before Thursday.",
      time: '1d ago',
      avatar: 'M',
      isTeacher: true,
    },
    { id: 'msg-31', author: 'Hugo Persson', text: 'Thanks! Is the Thirty Years War going to be on the mid-term exam?', time: '20h ago', avatar: 'H' },
    {
      id: 'msg-32',
      author: 'Maria Svensson',
      text: "Yes, it's one of the key topics. Focus on causes, key battles, and the Peace of Westphalia.",
      time: '18h ago',
      avatar: 'M',
      isTeacher: true,
    },
  ],
  'demo-9a-science-discussion': [
    {
      id: 'msg-40',
      author: 'Lars Nilsson',
      text: "Lab groups for next week's experiment are posted. Check the Files tab for your assigned group and materials list.",
      time: '3h ago',
      avatar: 'L',
      isTeacher: true,
    },
    { id: 'msg-41', author: 'Astrid Nilsson', text: 'Do we need to bring safety goggles from home or are they provided?', time: '2h ago', avatar: 'A' },
    {
      id: 'msg-42',
      author: 'Lars Nilsson',
      text: 'All safety equipment is provided. Just bring your lab notebook and a pen.',
      time: '1h ago',
      avatar: 'L',
      isTeacher: true,
    },
    {
      id: 'msg-43',
      author: 'Oliver Gustafsson',
      text: "I'm in Group 3 but I'll be away Thursday. Can I switch to Group 2 on Wednesday?",
      time: '30min ago',
      avatar: 'O',
    },
  ],
};

// ─── Files mock data ────────────────────────────────────────────────────────

export const MOCK_FILES = {
  'demo-9a-math': [
    { id: 'file-1', name: 'Chapter 3 — Solutions.pdf', size: '2.4 MB', uploadedBy: 'Anna Lindberg', uploadedAt: '2 days ago', type: 'pdf' },
    { id: 'file-2', name: 'Exercise 4.3 — Video Explanation.mp4', size: '48 MB', uploadedBy: 'Anna Lindberg', uploadedAt: '1 hour ago', type: 'video' },
    { id: 'file-3', name: 'Formula Sheet — Algebra.pdf', size: '340 KB', uploadedBy: 'Anna Lindberg', uploadedAt: '1 week ago', type: 'pdf' },
    { id: 'file-4', name: 'Week 6 Homework.pdf', size: '890 KB', uploadedBy: 'Anna Lindberg', uploadedAt: '3 days ago', type: 'pdf' },
  ],
  'demo-9a-swedish': [
    { id: 'file-10', name: 'Essay Writing Guide.pdf', size: '1.2 MB', uploadedBy: 'Erik Johansson', uploadedAt: '1 week ago', type: 'pdf' },
    { id: 'file-11', name: 'Freja — Draft Essay.docx', size: '56 KB', uploadedBy: 'Freja Karlsson', uploadedAt: '1 hour ago', type: 'doc' },
    { id: 'file-12', name: 'Source Citations Template.docx', size: '28 KB', uploadedBy: 'Erik Johansson', uploadedAt: '5 days ago', type: 'doc' },
  ],
  'demo-9a-english': [
    { id: 'file-20', name: 'Vocabulary List Ch 5-6.pdf', size: '420 KB', uploadedBy: 'Anna Lindberg', uploadedAt: '1 week ago', type: 'pdf' },
    { id: 'file-21', name: 'Study Guide — Test Prep.pdf', size: '680 KB', uploadedBy: 'Anna Lindberg', uploadedAt: '1 week ago', type: 'pdf' },
    { id: 'file-22', name: 'Reading Comprehension — Ch 4.pdf', size: '1.1 MB', uploadedBy: 'Anna Lindberg', uploadedAt: '2 weeks ago', type: 'pdf' },
  ],
  'demo-9a-history': [
    { id: 'file-30', name: 'Thirty Years War — Timeline.pdf', size: '3.2 MB', uploadedBy: 'Maria Svensson', uploadedAt: '3 days ago', type: 'pdf' },
    { id: 'file-31', name: 'Lecture Slides — Week 6.pptx', size: '8.4 MB', uploadedBy: 'Maria Svensson', uploadedAt: '1 day ago', type: 'ppt' },
    { id: 'file-32', name: 'Mid-term Study Guide.pdf', size: '1.8 MB', uploadedBy: 'Maria Svensson', uploadedAt: '4 days ago', type: 'pdf' },
  ],
  'demo-9a-science': [
    { id: 'file-40', name: 'Lab Groups — Week 7.pdf', size: '180 KB', uploadedBy: 'Lars Nilsson', uploadedAt: '3 hours ago', type: 'pdf' },
    { id: 'file-41', name: 'Experiment 2 — Materials List.pdf', size: '240 KB', uploadedBy: 'Lars Nilsson', uploadedAt: '3 hours ago', type: 'pdf' },
    { id: 'file-42', name: 'Lab Report Template.docx', size: '45 KB', uploadedBy: 'Lars Nilsson', uploadedAt: '2 weeks ago', type: 'doc' },
    { id: 'file-43', name: 'Safety Guidelines.pdf', size: '520 KB', uploadedBy: 'Lars Nilsson', uploadedAt: '1 month ago', type: 'pdf' },
  ],
};

// ─── Assignment Submissions (teacher view) ──────────────────────────────────

const GRADES = ['A', 'B', 'C', 'D', 'E', 'F'];

const generateSubmissions = (students, seed = 0) => {
  return students.map((student, i) => {
    const idx = (seed + i) % 17;
    if (idx < 4) {
      // Graded
      const grade = GRADES[idx % 5];
      const score = Math.max(40, 100 - idx * 8 - (i % 7));
      return {
        studentId: student.id,
        studentName: student.name,
        status: 'graded',
        grade,
        score,
        submittedAt: `${idx + 1} days ago`,
        feedback: idx === 0 ? 'Excellent work! Very thorough analysis.' : idx === 1 ? 'Good effort, but review section 3.' : null,
      };
    } else if (idx < 9) {
      // Submitted, awaiting review
      return {
        studentId: student.id,
        studentName: student.name,
        status: 'submitted',
        submittedAt: `${Math.max(1, idx - 3)} days ago`,
      };
    } else if (idx < 13) {
      // Late submission
      return {
        studentId: student.id,
        studentName: student.name,
        status: 'late',
        submittedAt: `${idx - 6} days late`,
      };
    } else {
      // Not submitted
      return {
        studentId: student.id,
        studentName: student.name,
        status: 'pending',
      };
    }
  });
};

const generateQuizAttempts = (students, questionCount, timeLimitMin, seed = 0) => {
  return students.map((student, i) => {
    const idx = (seed + i) % 13;
    if (idx < 8) {
      // Completed
      const score = Math.max(30, Math.min(100, 95 - idx * 7 - (i % 5) * 3));
      const correctAnswers = Math.round((score / 100) * questionCount);
      const minutes = Math.max(5, Math.min(timeLimitMin || 30, 10 + idx * 3 + (i % 4)));
      const seconds = (idx * 17 + i * 11) % 60;
      return {
        studentId: student.id,
        studentName: student.name,
        status: 'completed',
        score,
        correctAnswers,
        totalQuestions: questionCount,
        timeSpent: `${minutes}:${String(seconds).padStart(2, '0')}`,
        submittedAt: `${Math.max(1, idx)} days ago`,
      };
    } else if (idx < 10) {
      // In progress
      return {
        studentId: student.id,
        studentName: student.name,
        status: 'in_progress',
        timeSpent: `${5 + idx}:${String((idx * 23) % 60).padStart(2, '0')}`,
      };
    } else {
      // Not started
      return {
        studentId: student.id,
        studentName: student.name,
        status: 'not_started',
      };
    }
  });
};

export const getMockSubmissions = (pageId, students) => {
  // Deterministic seed from pageId
  const seed = pageId.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return generateSubmissions(students, seed);
};

export const getMockQuizAttempts = (pageId, students, questionCount = 10, timeLimitMin = 30) => {
  const seed = pageId.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return generateQuizAttempts(students, questionCount, timeLimitMin, seed);
};

// ─── Sections (TOC) ─────────────────────────────────────────────────────────

const SECTIONS_9A = [
  createSubjectPages('Mathematics', 'demo-9a-math', {
    assignmentProgress: 0.6,
    quizProgress: null,
    weekNumber: 6,
    pastSessionCount: 4,
    discussionMessages: 5,
    fileCount: 4,
  }),
  createSubjectPages('Swedish', 'demo-9a-swedish', {
    assignmentProgress: 0.3,
    quizProgress: 0.9,
    weekNumber: 6,
    pastSessionCount: 3,
    discussionMessages: 4,
    fileCount: 3,
  }),
  createSubjectPages('English', 'demo-9a-english', {
    assignmentProgress: null,
    quizProgress: null,
    weekNumber: 6,
    pastSessionCount: 3,
    discussionMessages: 3,
    fileCount: 3,
  }),
  createSubjectPages('History', 'demo-9a-history', {
    assignmentProgress: 0.7,
    quizProgress: 1,
    weekNumber: 6,
    pastSessionCount: 4,
    discussionMessages: 3,
    fileCount: 3,
  }),
  createSubjectPages('Science', 'demo-9a-science', {
    assignmentProgress: 0.4,
    quizProgress: 0.5,
    weekNumber: 6,
    pastSessionCount: 2,
    discussionMessages: 4,
    fileCount: 4,
  }),
];

const SECTIONS_9B = [
  createSubjectPages('Mathematics', 'demo-9b-math', {
    assignmentProgress: 0.45,
    quizProgress: null,
    weekNumber: 5,
    pastSessionCount: 3,
    discussionMessages: 3,
    fileCount: 3,
  }),
  createSubjectPages('Swedish', 'demo-9b-swedish', {
    assignmentProgress: 0.8,
    quizProgress: 1,
    weekNumber: 5,
    pastSessionCount: 2,
    discussionMessages: 2,
    fileCount: 2,
  }),
  createSubjectPages('Science', 'demo-9b-science', {
    assignmentProgress: null,
    quizProgress: null,
    isLocked: true,
    weekNumber: 5,
    pastSessionCount: 1,
    discussionMessages: 0,
    fileCount: 2,
  }),
];

const SECTIONS_TUTORING = [
  createSubjectPages('Mathematics — Extra Support', 'demo-tutoring-math', {
    assignmentProgress: 0.2,
    quizProgress: null,
    weekNumber: 4,
    pastSessionCount: 2,
    discussionMessages: 2,
    fileCount: 1,
  }),
  createSubjectPages('Reading Comprehension', 'demo-tutoring-reading', {
    assignmentProgress: 0.5,
    quizProgress: null,
    weekNumber: 4,
    pastSessionCount: 2,
    discussionMessages: 1,
    fileCount: 2,
  }),
];

// ─── Classroom list ─────────────────────────────────────────────────────────

export const DEMO_CLASSROOMS = [
  {
    id: 'demo-classroom-9a',
    name: '9A — Spring 2025',
    studentCount: MOCK_STUDENTS_9A.length,
    courseCount: SECTIONS_9A.length,
    description: 'Main classroom for 9A with all core subjects',
  },
  {
    id: 'demo-classroom-9b',
    name: '9B — Spring 2025',
    studentCount: MOCK_STUDENTS_9B.length,
    courseCount: SECTIONS_9B.length,
    description: 'Main classroom for 9B',
  },
  {
    id: 'demo-classroom-tutoring',
    name: 'Tutoring Group — Extra Support',
    studentCount: MOCK_STUDENTS_TUTORING.length,
    courseCount: SECTIONS_TUTORING.length,
    description: 'Small group tutoring for students needing extra help',
  },
];

// ─── Full classroom detail (used by useClassroom hook) ──────────────────────

export const DEMO_CLASSROOM_DETAILS = {
  'demo-classroom-9a': {
    group: {
      id: 'demo-classroom-9a',
      name: '9A — Spring 2025',
      description: 'Main classroom for 9A with all core subjects. Live lessons Mon–Fri.',
    },
    courses: [{ id: 'demo-course-9a', name: '9A Curriculum' }],
    courseId: 'demo-course-9a',
    courseDetail: { id: 'demo-course-9a', name: '9A Curriculum', toc: SECTIONS_9A },
    members: [...MOCK_TEACHERS, ...MOCK_STUDENTS_9A],
    sections: SECTIONS_9A,
  },
  'demo-classroom-9b': {
    group: {
      id: 'demo-classroom-9b',
      name: '9B — Spring 2025',
      description: 'Main classroom for 9B.',
    },
    courses: [{ id: 'demo-course-9b', name: '9B Curriculum' }],
    courseId: 'demo-course-9b',
    courseDetail: { id: 'demo-course-9b', name: '9B Curriculum', toc: SECTIONS_9B },
    members: [MOCK_TEACHERS[0], MOCK_TEACHERS[1], MOCK_TEACHERS[3], ...MOCK_STUDENTS_9B],
    sections: SECTIONS_9B,
  },
  'demo-classroom-tutoring': {
    group: {
      id: 'demo-classroom-tutoring',
      name: 'Tutoring Group — Extra Support',
      description: 'Small group tutoring sessions for mathematics and reading comprehension.',
    },
    courses: [{ id: 'demo-course-tutoring', name: 'Extra Support Curriculum' }],
    courseId: 'demo-course-tutoring',
    courseDetail: { id: 'demo-course-tutoring', name: 'Extra Support Curriculum', toc: SECTIONS_TUTORING },
    members: [MOCK_TEACHERS[0], MOCK_TEACHERS[1], ...MOCK_STUDENTS_TUTORING],
    sections: SECTIONS_TUTORING,
  },
};

// ─── Check if an ID is a demo ID ───────────────────────────────────────────

export const isDemoClassroom = (groupId) => typeof groupId === 'string' && groupId.startsWith('demo-classroom-');
